import { ReactNode, memo, useMemo } from 'react'
import { useDrag } from 'react-dnd'
import { Trans } from 'react-i18next'

import { Nullable } from '@types'

import {
  Shortlist,
  ShortlistCategoriesConfigProps,
  ShortlistExtendedProfile,
  ShortlistTableConfigProps,
} from '@modules/shortlist/types'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'
import { Row, flexRender } from '@tanstack/react-table'

import { HighlightedRowLabel, StyledTableCell, StyledTableRow, TableCellInner } from './index.styles'

export interface DraggableTableRowProps {
  row: Row<ShortlistTableConfigProps>
  optionalColumnsConfig?: Nullable<ShortlistCategoriesConfigProps>
  tableColumnsComponents: Record<ShortlistTableConfigProps['fieldName'], () => ReactNode>
  profileData: ShortlistExtendedProfile & { isProfileUpdated: boolean }
  highlightedRowLabel?: string
  activeShortlist?: Shortlist
  meta?: unknown
}

const DraggableTableRow = ({ row, optionalColumnsConfig, profileData, highlightedRowLabel, meta }: DraggableTableRowProps) => {
  const { profileId, profileUpdatedDate, profileStatus, isProfileUpdated } = profileData
  const { isDesktop } = useScreenDimensions()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tableColumnsConfig = useMemo(() => row.getVisibleCells(), [row.original])

  const draggableCell = useMemo(
    () => tableColumnsConfig.find(config => config.column.id === 'profileTitle')?.column.columnDef.cell,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [row.id, tableColumnsConfig],
  )

  const [{ isDragging }, drag] = useDrag(() => ({
    canDrag: isDesktop,
    type: 'profile',
    item: {
      profileId,
      renderer: draggableCell,
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  }))

  const isActive = profileStatus === 'active'

  const cells = useMemo(
    () =>
      tableColumnsConfig.map((tableColumn, idx: number) => {
        // @ts-expect-error TODO: column def typing
        const { restCellProps, fieldName, isEnabled, isDragHandle: rawIsDragHandle, customDisable } = tableColumn.column.columnDef
        const isDragHandle = rawIsDragHandle && isDesktop

        if (
          !(
            isEnabled ||
            (optionalColumnsConfig && optionalColumnsConfig[fieldName as keyof ShortlistCategoriesConfigProps]?.visible)
          )
        )
          return null

        const extraStyles =
          (isDragging && isDragHandle && { cursor: 'grabbing' }) || (isDragHandle && { cursor: 'pointer' }) || {}

        return (
          <StyledTableCell
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...restCellProps}
            key={profileId + fieldName + profileUpdatedDate}
            data-testid={`${fieldName}_testid`}
            $isDisabled={!isActive && !customDisable}
            $isHighlighted={isProfileUpdated}
          >
            {/* @ts-expect-error children type */}
            <TableCellInner
              style={extraStyles}
              data-testid={`${fieldName}_testid_inner`}
              draggable={isDragHandle && isDesktop}
              ref={isDragHandle ? drag : null}
            >
              {tableColumn.column.columnDef.cell
                ? flexRender(tableColumn.column.columnDef.cell, { ...tableColumn.getContext(), meta })
                : tableColumn.getValue()}
            </TableCellInner>
            {idx === 0 && isProfileUpdated && highlightedRowLabel ? (
              <HighlightedRowLabel>
                <Trans>{highlightedRowLabel}</Trans>
              </HighlightedRowLabel>
            ) : null}
          </StyledTableCell>
        )
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableColumnsConfig, isProfileUpdated, meta, profileUpdatedDate, highlightedRowLabel, isDesktop],
  )

  return (
    <StyledTableRow key={profileId + profileUpdatedDate} $isActive={isActive} $isHighlighted={isProfileUpdated}>
      {cells}
    </StyledTableRow>
  )
}

export default memo(DraggableTableRow)
